/**
 * RegelTree.tsx — Bedingungsbaum renderer for AHB expressions.
 *
 * Two parts:
 *  1. `buildRegelTree` — a PURE, deterministic layout helper (no DOM, no React)
 *     that turns a parsed `AhbExpression` + a `resolve` closure into a positioned
 *     graph (nodes + elbow edges). Exported so it can be unit-tested.
 *  2. `RegelTree` — an SVG component that draws the graph in the EbdDiagram look
 *     (rounded rects, badge strips, wrapped body text, orthogonal elbow edges),
 *     with a simple drag-pan + Ctrl/⌘-wheel zoom.
 *
 * Layout shape (mirrors EbdGraph): root centered on top, one combinator node
 * centered below it, and the leaf nodes spread evenly along a row at the bottom.
 */
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { wrapText } from "@/pages/Ebd/ebdLayout";
import { parseAhbExpression } from "@/lib/ahbExpression";
import type { AhbExpression, AhbExprNode, ConditionKind } from "@/types/regelwerk";

// ── graph model ──────────────────────────────────────────────────────────────
export type RegelNodeKind = "root" | "combinator" | ConditionKind;

export interface RegelNode {
  id: string;
  kind: RegelNodeKind;
  x: number;
  y: number;
  w: number;
  h: number;
  badge?: string; // FORMAT / VORAUSSETZUNG / PAKET, or combinator op
  lines: string[]; // wrapped body text (leaf) — empty for root/combinator
  title?: string; // leaf key (e.g. "931") rendered next to the badge
}

export interface RegelPoint {
  x: number;
  y: number;
}

export interface RegelEdge {
  id: string;
  from: string;
  to: string;
  points: RegelPoint[];
}

export interface RegelGraph {
  nodes: RegelNode[];
  edges: RegelEdge[];
  width: number;
  height: number;
}

// ── geometry constants ───────────────────────────────────────────────────────
const MARGIN = 40;
const ROOT_W = 300;
const COMBI_MIN_W = 200;
const LEAF_W = 230;
const LEAF_GAP = 40;
/** horizontal gap between sibling subtrees */
const SIBLING_GAP = LEAF_GAP;
const PAD_X = 16;
const PAD_Y = 14;
const LINE_H = 20;
const BADGE_H = 24;
const ROW_GAP = 64;
const LEAF_CHARS = 30;
/** recursion guard for self-/mutually-referential packages */
const MAX_DEPTH = 8;

const rootH = PAD_Y * 2 + BADGE_H;
const combiH = PAD_Y * 2 + BADGE_H;
const leafHeight = (bodyLines: number) => PAD_Y * 2 + BADGE_H + bodyLines * LINE_H;

/** Top-operator → combinator label, matching the Bedingungsbaum screenshot. */
export function combinatorLabel(node: AhbExprNode | null): string {
  if (!node) return "WENN";
  switch (node.type) {
    case "and":
      return "WENN [ DANN AUCH ]";
    case "or":
      return "ODER";
    case "xor":
      return "ENTWEDER / ODER";
    default:
      return "WENN";
  }
}

/** Badge text for a resolved leaf kind. */
function badgeFor(kind: ConditionKind): string {
  switch (kind) {
    case "format":
      return "FORMAT";
    case "voraussetzung":
      return "VORAUSSETZUNG";
    case "paket":
      return "PAKET";
    default:
      return "UNBEKANNT";
  }
}

// ── recursive layout ──────────────────────────────────────────────────────────
// A mutable layout node: the semantic shape (kind/badge/title/lines/children)
// is built first, then `measure`/`computeSubW`/`place` assign geometry.
interface LayoutNode extends RegelNode {
  children: LayoutNode[];
  /** width of the whole subtree rooted here (for tidy horizontal packing) */
  subW: number;
}

/** Width of a header (root/combinator/package) box, sized to fit its badge. */
function headerWidth(badge: string | undefined): number {
  return Math.max(COMBI_MIN_W, approxBadgeW(badge ?? "") + PAD_X * 2);
}

/**
 * Build the positioned Bedingungsbaum graph from a parsed expression.
 *
 * The expression tree is rendered RECURSIVELY: every boolean combinator becomes
 * its own WENN/ODER node with its operands laid out beneath it, and every
 * package reference is expanded — its definition (itself a sub-expression of
 * conditions) is parsed and resolved into a child subtree. Leaves carry the
 * resolved condition text. This replaces the old flat two-level layout that
 * collapsed nested groups into a single "UNBEKANNT" box and never expanded
 * packages.
 *
 * @param expr    parsed AHB expression (`parseAhbExpression`)
 * @param resolve key → resolved `{ text, kind }` (id→text + classification);
 *                for a package id the `text` IS its sub-expression string.
 */
export function buildRegelTree(
  expr: AhbExpression,
  resolve: (key: string) => { text: string; kind: ConditionKind },
): RegelGraph {
  let idc = 0;
  const nid = () => `n${idc++}`;

  /** A resolved condition reference → leaf node. */
  const leafNode = (key: string, text: string, kind: ConditionKind): LayoutNode => ({
    id: nid(),
    kind,
    x: 0,
    y: 0,
    w: 0,
    h: 0,
    subW: 0,
    badge: badgeFor(kind),
    title: key,
    lines: wrapText(text, LEAF_CHARS),
    children: [],
  });

  /** Render one expression node into a layout subtree. */
  const render = (
    node: AhbExprNode,
    seenPkgs: ReadonlySet<string>,
    depth: number,
  ): LayoutNode => {
    if (node.type === "and" || node.type === "or" || node.type === "xor") {
      return {
        id: nid(),
        kind: "combinator",
        x: 0,
        y: 0,
        w: 0,
        h: 0,
        subW: 0,
        badge: combinatorLabel(node),
        lines: [],
        children: node.children.map((c) => render(c, seenPkgs, depth + 1)),
      };
    }

    if (node.type === "ref" || node.type === "package") {
      const { text, kind } = resolve(node.key);
      const title =
        node.type === "package" && node.cardinality
          ? `${node.key} · ${node.cardinality}`
          : node.key;

      // A package — either parsed as one (`9P`) or any id that resolves to a
      // package definition (e.g. `UB1`, a UB-Bedingung). Expand its
      // sub-expression so the referenced conditions are shown and resolved.
      if (kind === "paket") {
        const sub = parseAhbExpression(text);
        const canExpand =
          !seenPkgs.has(node.key) && depth < MAX_DEPTH && !!sub.tree;
        if (canExpand) {
          const nextSeen = new Set(seenPkgs).add(node.key);
          return {
            id: nid(),
            kind: "paket",
            x: 0,
            y: 0,
            w: 0,
            h: 0,
            subW: 0,
            badge: "PAKET",
            title,
            lines: [],
            children: [render(sub.tree!, nextSeen, depth + 1)],
          };
        }
        // Recursion guard or empty definition: show the package as a leaf.
        const body = (text ?? "").trim() || "—";
        return { ...leafNode(title, body, "paket"), title };
      }

      // A package-shaped id with no definition stays "unknown"; a plain
      // condition ref resolves to its text + classification.
      if (node.type === "package") {
        return { ...leafNode(title, (text ?? "").trim() || "—", "unknown"), title };
      }
      return leafNode(node.key, text, kind);
    }

    // Defensive: well-formed trees never reach here.
    return leafNode("", "—", "unknown");
  };

  // ── root node (raw expression label) ───────────────────────────────────────
  const root: LayoutNode = {
    id: "root",
    kind: "root",
    x: 0,
    y: 0,
    w: 0,
    h: 0,
    subW: 0,
    title: expr.raw.trim() || "—",
    lines: [],
    children: expr.tree ? [render(expr.tree, new Set(), 0)] : [],
  };

  // ── measure: assign each node its own box size ─────────────────────────────
  const measure = (n: LayoutNode): void => {
    if (n.kind === "root") {
      n.w = ROOT_W;
      n.h = rootH;
    } else if (n.kind === "combinator" || (n.kind === "paket" && n.children.length)) {
      n.w = headerWidth(n.badge);
      n.h = combiH;
    } else {
      n.w = LEAF_W;
      n.h = leafHeight(n.lines.length);
    }
    n.children.forEach(measure);
  };
  measure(root);

  // ── compute subtree widths (bottom-up) ─────────────────────────────────────
  const computeSubW = (n: LayoutNode): void => {
    n.children.forEach(computeSubW);
    if (n.children.length === 0) {
      n.subW = n.w;
      return;
    }
    const childrenW =
      n.children.reduce((s, c) => s + c.subW, 0) + SIBLING_GAP * (n.children.length - 1);
    n.subW = Math.max(n.w, childrenW);
  };
  computeSubW(root);

  // ── place: assign absolute x/y, centering each node over its subtree ───────
  const nodes: RegelNode[] = [];
  const edges: RegelEdge[] = [];
  const place = (n: LayoutNode, leftX: number, topY: number): void => {
    n.y = topY;
    n.x = leftX + (n.subW - n.w) / 2;
    nodes.push(n);
    if (n.children.length === 0) return;
    const childrenW =
      n.children.reduce((s, c) => s + c.subW, 0) + SIBLING_GAP * (n.children.length - 1);
    const childTop = topY + n.h + ROW_GAP;
    let cursor = leftX + (n.subW - childrenW) / 2;
    for (const c of n.children) {
      place(c, cursor, childTop);
      edges.push(elbow(n, c, `${n.id}-${c.id}`));
      cursor += c.subW + SIBLING_GAP;
    }
  };
  place(root, MARGIN, MARGIN);

  const canvasW = root.subW + MARGIN * 2;
  const bottom = Math.max(...nodes.map((n) => n.y + n.h));
  return { nodes, edges, width: canvasW, height: bottom + MARGIN };
}

/** Orthogonal elbow edge: down from `from`'s bottom into `to`'s top. */
function elbow(from: RegelNode, to: RegelNode, key: string): RegelEdge {
  const sx = from.x + from.w / 2;
  const sy = from.y + from.h;
  const tx = to.x + to.w / 2;
  const ty = to.y;
  const points: RegelPoint[] =
    Math.abs(tx - sx) < 1
      ? [
          { x: sx, y: sy },
          { x: tx, y: ty },
        ]
      : (() => {
          const midY = (sy + ty) / 2;
          return [
            { x: sx, y: sy },
            { x: sx, y: midY },
            { x: tx, y: midY },
            { x: tx, y: ty },
          ];
        })();
  return { id: `e-${key}`, from: from.id, to: to.id, points };
}

// ── colors: kind → palette (EbdDiagram-style `C`) ─────────────────────────────
const C: Record<RegelNodeKind, { fill: string; stroke: string; text: string }> = {
  root: { fill: "#9fb0c3", stroke: "#7a8da1", text: "#16202c" },
  combinator: { fill: "#c3d0df", stroke: "#7a8da1", text: "#16202c" },
  format: { fill: "#cfe6cf", stroke: "#7faa7f", text: "#1c321c" }, // green-ish
  voraussetzung: { fill: "#cddfe0", stroke: "#7fa7a9", text: "#16292a" }, // petrol/neutral
  paket: { fill: "#f0e2c0", stroke: "#c2a85f", text: "#3a2f12" }, // amber
  unknown: { fill: "#e2e2e2", stroke: "#a8a8a8", text: "#33373b" },
};

const BADGE_FILL: Record<RegelNodeKind, string> = {
  root: "#5b7088",
  combinator: "#5b7088",
  format: "#3f7a3f",
  voraussetzung: "#3f7a7d",
  paket: "#9a7b22",
  unknown: "#74787c",
};

function pointsToPath(points: RegelPoint[]): string {
  return points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
}

function approxBadgeW(text: string): number {
  return Math.max(34, text.length * 8 + 16);
}

function RegelNodeShape({ n }: { n: RegelNode }) {
  const palette = C[n.kind];
  const badgeW = n.badge ? approxBadgeW(n.badge) : 0;
  const textTop = n.y + PAD_Y + BADGE_H;
  const isHeaderOnly = n.kind === "root" || n.kind === "combinator";
  return (
    <g>
      <rect
        x={n.x}
        y={n.y}
        width={n.w}
        height={n.h}
        rx={12}
        ry={12}
        fill={palette.fill}
        stroke={palette.stroke}
        strokeWidth={1.5}
      />

      {/* header badge */}
      {n.badge && (
        <g>
          <rect
            x={n.x + PAD_X}
            y={n.y + PAD_Y - 2}
            width={badgeW}
            height={BADGE_H}
            rx={6}
            ry={6}
            fill={BADGE_FILL[n.kind]}
          />
          <text
            x={n.x + PAD_X + badgeW / 2}
            y={n.y + PAD_Y + BADGE_H / 2 - 1}
            textAnchor="middle"
            dominantBaseline="central"
            fontWeight={700}
            fontSize={12}
            fill="#ffffff"
          >
            {n.badge}
          </text>
        </g>
      )}

      {/* title: raw expression (root) or leaf key, beside/after the badge */}
      {n.title && (
        <text
          x={n.x + PAD_X + (n.badge ? badgeW + 10 : 0)}
          y={n.y + PAD_Y + BADGE_H / 2 - 1}
          dominantBaseline="central"
          fontWeight={isHeaderOnly ? 700 : 600}
          fontSize={isHeaderOnly ? 15 : 14}
          fill={palette.text}
        >
          {n.title}
        </text>
      )}

      {/* leaf body text */}
      {n.lines.length > 0 && (
        <text fill={palette.text} fontSize={13}>
          {n.lines.map((line, i) => (
            <tspan key={i} x={n.x + PAD_X} y={textTop + i * LINE_H + 8}>
              {line}
            </tspan>
          ))}
        </text>
      )}
    </g>
  );
}

export const RegelTree = forwardRef<SVGSVGElement, { graph: RegelGraph }>(
  function RegelTree({ graph }, ref) {
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
    const canvasRef = useRef<HTMLDivElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Keep our flag in sync with the browser (covers Esc / external exit).
    useEffect(() => {
      const onChange = () =>
        setIsFullscreen(document.fullscreenElement === canvasRef.current);
      document.addEventListener("fullscreenchange", onChange);
      return () => document.removeEventListener("fullscreenchange", onChange);
    }, []);

    const toggleFullscreen = () => {
      const el = canvasRef.current;
      if (!el) return;
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
      } else {
        el.requestFullscreen?.();
      }
    };

    const onWheel = useCallback((e: React.WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoom((z) => Math.min(2.5, Math.max(0.2, z * (e.deltaY < 0 ? 1.1 : 0.9))));
    }, []);

    const onPointerDown = (e: React.PointerEvent) => {
      (e.target as Element).setPointerCapture?.(e.pointerId);
      drag.current = { x: e.clientX, y: e.clientY, ox: pan.x, oy: pan.y };
    };
    const onPointerMove = (e: React.PointerEvent) => {
      if (!drag.current) return;
      setPan({
        x: drag.current.ox + (e.clientX - drag.current.x),
        y: drag.current.oy + (e.clientY - drag.current.y),
      });
    };
    const endDrag = () => {
      drag.current = null;
    };

    const zoomBy = (f: number) => setZoom((z) => Math.min(2.5, Math.max(0.2, z * f)));
    const reset = () => {
      setZoom(1);
      setPan({ x: 0, y: 0 });
    };

    return (
      <div className="regel-canvas" ref={canvasRef} onWheel={onWheel}>
        <div className="regel-zoom-controls">
          <button type="button" onClick={() => zoomBy(1.15)} aria-label="Vergrößern" title="Vergrößern">+</button>
          <button type="button" onClick={() => zoomBy(0.87)} aria-label="Verkleinern" title="Verkleinern">−</button>
          <button
            type="button"
            className="regel-zoom-pct"
            onClick={reset}
            title="Ansicht zurücksetzen"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? "Vollbild beenden" : "Vollbild"}
            title={isFullscreen ? "Vollbild beenden" : "Vollbild"}
          >
            {isFullscreen ? "⤡" : "⤢"}
          </button>
        </div>

        <div
          className="regel-pan"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: "0 0",
            cursor: drag.current ? "grabbing" : "grab",
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
        >
          <svg
            ref={ref}
            width={graph.width}
            height={graph.height}
            viewBox={`0 0 ${graph.width} ${graph.height}`}
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: "block" }}
          >
            <defs>
              <marker
                id="regel-arrow"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="7"
                markerHeight="7"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#6f9a7f" />
              </marker>
            </defs>

            {graph.edges.map((e) => (
              <path
                key={e.id}
                d={pointsToPath(e.points)}
                fill="none"
                stroke="#6f9a7f"
                strokeWidth={1.8}
                markerEnd="url(#regel-arrow)"
              />
            ))}

            {graph.nodes.map((n) => (
              <RegelNodeShape key={n.id} n={n} />
            ))}
          </svg>
        </div>

        <div className="regel-hint">Strg/⌘ + Mausrad zum Zoomen · Ziehen zum Verschieben</div>
      </div>
    );
  },
);
